import type { HourlyPoint } from "@/lib/surf";
import { degToCompass } from "@/lib/surf";
import { Wind, Waves } from "lucide-react";

export const HourlyForecast = ({ hours }: { hours: HourlyPoint[] }) => {
  if (!hours.length) return null;
  const maxWave = Math.max(...hours.map(h => h.waveHeight), 0.5);
  const maxWind = Math.max(...hours.map(h => h.windSpeed), 5);

  const fmtHour = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "numeric", hour12: true }).replace(" ", "").toLowerCase();
  };

  return (
    <div className="rounded-3xl bg-card/80 backdrop-blur-md border border-border shadow-soft p-6">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Next 24 hours</div>
          <h3 className="font-display text-xl sm:text-2xl md:text-3xl text-foreground mt-1">Hourly Forecast</h3>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Waves className="h-3 w-3 text-ocean" /> Swell</span>
          <span className="flex items-center gap-1"><Wind className="h-3 w-3 text-sunset" /> Wind</span>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2 px-2 pb-2">
        <div className="flex gap-2 min-w-max">
          {hours.map(h => {
            const wavePct = (h.waveHeight / maxWave) * 100;
            const windPct = (h.windSpeed / maxWind) * 100;
            return (
              <div key={h.time} className="w-16 flex-shrink-0 rounded-2xl bg-background/60 border border-border p-2 flex flex-col items-center">
                <div className="text-[10px] font-bold uppercase text-muted-foreground">{fmtHour(h.time)}</div>

                <div className="relative h-24 w-full mt-2 flex items-end justify-center gap-1">
                  <div className="w-2.5 bg-gradient-ocean rounded-t-full transition-all" style={{ height: `${Math.max(4, wavePct)}%` }} />
                  <div className="w-2.5 bg-gradient-sunset rounded-t-full transition-all" style={{ height: `${Math.max(4, windPct)}%` }} />
                </div>

                <div className="mt-2 text-[11px] font-bold text-ocean-deep">{h.waveHeight.toFixed(1)}m</div>
                <div className="text-[10px] text-muted-foreground">{h.swellPeriod.toFixed(0)}s</div>
                <div className="mt-1 text-[10px] font-semibold text-foreground">{h.windSpeed.toFixed(0)}<span className="text-muted-foreground">km</span></div>
                <div
                  className="mt-1 text-[9px] text-muted-foreground"
                  title={`${h.windDirection}°`}
                >
                  <span className="inline-block" style={{ transform: `rotate(${h.windDirection + 180}deg)` }}>↑</span> {degToCompass(h.windDirection)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
